import { useState, type FormEvent } from "react";
import {
  fetchAuthSession,
  signIn,
  signOut,
  type SignInOutput,
} from "aws-amplify/auth";
import { ErrorMessage } from "./error-message.js";
import { friendlyAuthError } from "./cognito-errors.js";

export type SignInResult = SignInOutput;

interface Props {
  onResult: (result: SignInOutput, credentials: { username: string; password: string }) => void;
  /**
   * Called when Amplify reports the user is already signed in (the
   * recovery path for the post-MFA-setup race where the auth context
   * fell out of sync with Amplify's internal store). Caller should
   * refresh the auth context and navigate away from /signin.
   */
  onAlreadySignedIn?: () => void;
}

/**
 * Email + password sign-in form. Calls Amplify v6 `signIn` and bubbles
 * the `nextStep` up to the page-level state machine, which switches
 * between MfaChallengeForm and MfaSetupFlow based on `signInStep`.
 *
 * The form intentionally does NOT navigate on success — that's the
 * caller's responsibility (the page may need to show MFA challenge
 * first). Credentials are passed back so SignUpForm → auto-signIn flow
 * can also re-use the post-signup credentials.
 */
export function SignInForm({ onResult, onAlreadySignedIn }: Props) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      const result = await signIn({ username: email, password });
      onResult(result, { username: email, password });
    } catch (err) {
      const name =
        typeof err === "object" && err !== null && "name" in err
          ? String(err.name)
          : "";
      // UserAlreadyAuthenticatedException has two flavors:
      //   (a) live session exists — fetchAuthSession returns tokens.
      //       Recover by refreshing the auth context; the user is in fact
      //       signed in, just not visible to our context yet.
      //   (b) zombie state — Amplify's in-memory "I'm signed in" flag is
      //       set but no tokens are actually stored (e.g., a half-completed
      //       MFA setup). signIn will keep throwing this until we call
      //       signOut() to clear the in-memory flag, after which signIn
      //       can proceed normally.
      if (name === "UserAlreadyAuthenticatedException") {
        try {
          const session = await fetchAuthSession();
          if (session.tokens?.accessToken && onAlreadySignedIn) {
            onAlreadySignedIn();
            return;
          }
          // Zombie: clear Amplify state and retry signIn once.
          await signOut();
          const retried = await signIn({ username: email, password });
          onResult(retried, { username: email, password });
          return;
        } catch (retryErr) {
          setError(friendlyAuthError(retryErr));
          return;
        }
      }
      setError(friendlyAuthError(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={(e) => void onSubmit(e)} className="space-y-4">
      <div>
        <label htmlFor="signin-email" className="mb-1 block text-sm font-medium">
          Email
        </label>
        <input
          id="signin-email"
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full rounded-md border border-zinc-300 px-3 py-2 text-base shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-zinc-700 dark:bg-zinc-900"
          disabled={submitting}
        />
      </div>
      <div>
        <label htmlFor="signin-password" className="mb-1 block text-sm font-medium">
          Password
        </label>
        <input
          id="signin-password"
          type="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="w-full rounded-md border border-zinc-300 px-3 py-2 text-base shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-zinc-700 dark:bg-zinc-900"
          disabled={submitting}
        />
      </div>
      {error && <ErrorMessage>{error}</ErrorMessage>}
      <button
        type="submit"
        disabled={submitting}
        className="w-full rounded-md bg-brand-500 px-4 py-2.5 text-base font-medium text-white shadow-sm transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {submitting ? "Signing in…" : "Sign in"}
      </button>
    </form>
  );
}
